/**
 * @module Utils.creditBundles
 *
 * Utility functions for grouping and transforming credit bundle data
 * from the new flat API response format to grouped format for display.
 */

import {
  CreditBundle,
  GroupedByProduct,
  GroupedRateLink,
  Offering,
  OfferingRateLink,
} from "app/models/scheduling/CreditBundleModel";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import { parseServerAmount } from "app/utils/currency";

/**
 * CN-2641: order bundles the way the backend consumes them — soonest expiry
 * first, never-expiring last. Ties keep the incoming (API) order, which is
 * already id-ascending. ISO timestamps compare correctly as strings.
 */
export function sortBundlesExpiringFirst(bundles: CreditBundle[]): CreditBundle[] {
  return [...bundles].sort((a, b) => {
    if (!a.expiredAt && !b.expiredAt) {return 0;}
    if (!a.expiredAt) {return 1;}
    if (!b.expiredAt) {return -1;}
    return a.expiredAt.localeCompare(b.expiredAt);
  });
}

/**
 * Groups credit bundles by product ID.
 * Aggregates remaining sessions and rate links per product.
 */
export function groupByProduct(bundles: CreditBundle[]): GroupedByProduct[] {
  const grouped = new Map<string, GroupedByProduct>();

  for (const bundle of bundles) {
    // CFTB-3317: group by product AND owning student so a parent and a dependent
    // who hold the same offering get their own cards. For a single student this
    // is identical to grouping by product alone (one studentProfileId).
    const groupKey = `${bundle.product.id}::${bundle.studentProfileId}`;
    const existing = grouped.get(groupKey);
    const isCancelled = bundle.status === "cancelled";

    if (existing) {
      existing.creditBundleIds.push(bundle.id);
      existing.totalRemainingSessions += bundle.remainingSessions;
      existing.hasUnlimited = existing.hasUnlimited || bundle.unlimited;
      // Product is completed only if ALL bundles are completed
      existing.completed = existing.completed && bundle.completed;
      // Product is cancelled only if ALL bundles are cancelled. A run assigned
      // after a cancelled one is live, and the coach must be able to cancel it.
      existing.cancelled = existing.cancelled && isCancelled;

      // Keep the latest renewal date
      if (
        bundle.renewalDate &&
        (!existing.nextRenewalDate ||
          bundle.renewalDate > existing.nextRenewalDate)
      ) {
        existing.nextRenewalDate = bundle.renewalDate;
      }

      // CFTB-3423: keep the SOONEST expiry — that is the one the athlete is
      // about to lose, and the one the backend consumes first. A never-expiring
      // sibling must not erase a dated one. This used to keep whichever bundle
      // arrived first, which was only the soonest because Checkout happens to
      // sort before grouping.
      if (
        bundle.expiredAt &&
        (!existing.expiredAt || bundle.expiredAt < existing.expiredAt)
      ) {
        existing.expiredAt = bundle.expiredAt;
      }

      // Add to rate link groups
      addToRateLinkGroup(existing.rateLinkGroups, bundle);
    } else {
      const rateLinkGroups: GroupedRateLink[] = [];
      addToRateLinkGroup(rateLinkGroups, bundle);

      grouped.set(groupKey, {
        product: bundle.product,
        coach: bundle.coachProfile,
        owner: bundle.owner,
        studentProfileId: bundle.studentProfileId,
        studentFullName: bundle.studentFullName,
        creditBundleIds: [bundle.id],
        totalRemainingSessions: bundle.remainingSessions,
        hasUnlimited: bundle.unlimited,
        nextRenewalDate: bundle.renewalDate,
        // CN-2641: end date of a capped subscription run (null when uncapped).
        subscriptionEndsAt: bundle.subscriptionEndsAt,
        expiredAt: bundle.expiredAt,
        rateLinkGroups,
        completed: bundle.completed,
        cancelled: isCancelled,
      });
    }
  }

  return Array.from(grouped.values());
}

/**
 * Helper to add a bundle to the appropriate rate link group.
 * Only creates groups for bundles that have actual rate links.
 */
function addToRateLinkGroup(
  groups: GroupedRateLink[],
  bundle: CreditBundle
): void {
  const rateLinkId = bundle.stripeProductRateLinkId;

  // Skip bundles without rate links (e.g., one-time products without sessions)
  if (rateLinkId === null) {
    return;
  }

  const existingGroup = groups.find((g) => g.rateLinkId === rateLinkId);

  const carried = bundle.carriedOver ? bundle.remainingSessions : 0;

  if (existingGroup) {
    existingGroup.creditBundleIds.push(bundle.id);
    existingGroup.totalRemainingSessions += bundle.remainingSessions;
    existingGroup.carriedOverSessions += carried;
    existingGroup.hasUnlimited = existingGroup.hasUnlimited || bundle.unlimited;
  } else {
    groups.push({
      rateLinkId,
      title: bundle.lessonRate?.title || "Sessions",
      totalRemainingSessions: bundle.remainingSessions,
      carriedOverSessions: carried,
      hasUnlimited: bundle.unlimited,
      creditBundleIds: [bundle.id],
    });
  }
}

/**
 * Converts billing frequency from recurring string to number of months.
 * Handles both formats: "monthly"/"yearly" and "1"/"12"
 */
function recurringToMonths(recurring: string | null): number | null {
  if (!recurring) {
    return null;
  }
  if (recurring === "monthly" || recurring === "1") {
    return 1;
  }
  if (recurring === "yearly" || recurring === "12") {
    return 12;
  }
  // Try to parse as number for other billing frequencies
  const parsed = parseInt(recurring, 10);
  if (!isNaN(parsed) && parsed > 0) {
    return parsed;
  }
  return null;
}

/**
 * Transforms a grouped bundle into the Offering interface
 * that existing components expect.
 */
export function transformGroupedBundleToOffering(
  grouped: GroupedByProduct
): Offering {
  const rateLinks: OfferingRateLink[] = grouped.rateLinkGroups.map((rl) => ({
    id: rl.rateLinkId,
    title: rl.title,
    remainingSessions: rl.totalRemainingSessions,
    carriedOverSessions: rl.carriedOverSessions,
    unlimited: rl.hasUnlimited,
    quantity: rl.totalRemainingSessions,
    duration: "",
  }));

  return {
    offeringTitle: grouped.product.title,
    productType: grouped.product.productType,
    billingFrequency: recurringToMonths(grouped.product.recurring),
    nextInvoiceDate: grouped.nextRenewalDate,
    subscriptionEndsAt: grouped.subscriptionEndsAt,
    expiredAt: grouped.expiredAt,
    productPrice: parseServerAmount(grouped.product.price),
    currency: grouped.product.currency,
    remainingSessions: grouped.totalRemainingSessions,
    unlimited: grouped.hasUnlimited,
    completed: grouped.completed,
    cancelled: grouped.cancelled,
    productId: String(grouped.product.id),
    // productIntegerId is used for session management API calls
    productIntegerId: String(grouped.product.id),
    creditBundleIds: grouped.creditBundleIds,
    rateLinks,
    // Owner info (for student view) - use owner.name as primary, fall back to coach for profile picture
    fullName: grouped.owner.name,
    profilePicture: grouped.coach?.profilePicture ?? null,
    coachId: grouped.coach?.id,
    ownerType: grouped.owner.type,
    studentProfileId: grouped.studentProfileId,
    studentFullName: grouped.studentFullName,
    // Time restriction fields
    restrictWeekdays: grouped.product.restrictWeekdays,
    restrictWeekends: grouped.product.restrictWeekends,
    weekdayBeginAt: grouped.product.weekdayBeginAt,
    weekdayEndAt: grouped.product.weekdayEndAt,
  };
}

/**
 * Transforms an array of credit bundles into an array of Offerings.
 * Groups by product first, then transforms each group.
 */
export function transformCreditBundlesToOfferings(
  bundles: CreditBundle[]
): Offering[] {
  const grouped = groupByProduct(bundles);
  return grouped.map(transformGroupedBundleToOffering);
}

// CN-2832: what payment history carries for a subscription signed up before
// its custom start date (see PurchasePresenter#scheduled_subscription).
export interface ScheduledSubscription {
  productId: number;
  productTitle: string;
  recurring: string | null;
  price: string;
  currency: string;
  billingStartsAt: string;
  coachId: string | null;
  ownerName: string | null;
  studentProfileId: string;
  participantStudentProfileId: string | null;
  participantFullName: string | null;
}

/**
 * CN-2832: a scheduled subscription has no credits yet, so the bundle-based
 * offerings above never include it. Build its card from payment history.
 */
export function scheduledOfferingsFromPurchases(
  purchases: Array<{ scheduledSubscription?: ScheduledSubscription | null }>,
): Offering[] {
  return (purchases || [])
    .map((purchase) => purchase.scheduledSubscription)
    .filter((scheduled): scheduled is ScheduledSubscription => !!scheduled)
    .map((scheduled) => ({
      offeringTitle: scheduled.productTitle,
      productType: "subscription" as const,
      billingFrequency: recurringToMonths(scheduled.recurring),
      nextInvoiceDate: scheduled.billingStartsAt,
      billingStartsAt: scheduled.billingStartsAt,
      expiredAt: null,
      productPrice: parseServerAmount(scheduled.price),
      currency: scheduled.currency,
      remainingSessions: 0,
      unlimited: false,
      completed: false,
      cancelled: false,
      productId: String(scheduled.productId),
      productIntegerId: String(scheduled.productId),
      creditBundleIds: [],
      rateLinks: [],
      coachId: scheduled.coachId ?? undefined,
      fullName: scheduled.ownerName ?? undefined,
      studentProfileId: scheduled.participantStudentProfileId || scheduled.studentProfileId,
      studentFullName: scheduled.participantFullName,
    }));
}

/**
 * Filters offerings to show only active ones (subscriptions or with remaining sessions).
 */
export function filterActiveOfferings(offerings: Offering[]): Offering[] {
  return offerings.filter(
    (offering) =>
      offering.productType === "subscription" || offering.remainingSessions > 0
  );
}

export interface PackageCredit {
  remainingSessions: number;
  unlimited: boolean;
}

/**
 * Maps lesson rate ids to the athlete's applicable package/subscription credits.
 * Only counts usable bundles (not completed, cancelled, expired, or exhausted),
 * aggregating remaining sessions across bundles tied to the same lesson rate.
 */
export function getPackageCreditsByLessonRate(
  bundles: CreditBundle[]
): Map<number, PackageCredit> {
  const credits = new Map<number, PackageCredit>();
  const now = new Date();

  for (const bundle of bundles) {
    const rateId = bundle.lessonRate?.id;
    if (rateId == null) {
      continue;
    }
    if (bundle.completed || bundle.status === "cancelled") {
      continue;
    }
    if (!bundle.unlimited && bundle.remainingSessions <= 0) {
      continue;
    }
    if (bundle.expiredAt && new Date(bundle.expiredAt) < now) {
      continue;
    }

    const existing = credits.get(rateId);
    if (existing) {
      existing.remainingSessions += bundle.remainingSessions;
      existing.unlimited = existing.unlimited || bundle.unlimited;
    } else {
      credits.set(rateId, {
        remainingSessions: bundle.remainingSessions,
        unlimited: bundle.unlimited,
      });
    }
  }

  return credits;
}

/**
 * The lesson-rate fields needed to derive package-credit state. Any
 * `LessonRateModel` satisfies this structurally.
 */
export type PackageRate = Pick<LessonRateModel, "id" | "collectType">;

/**
 * Returns the athlete's applicable package credit for a lesson rate, or
 * undefined when none applies. Used to show the "Package: N remaining /
 * Unlimited" indicator on a booking option.
 *
 * Membership in the credit map is the authoritative signal: a credit bundle is
 * sold against a specific lesson rate, so holding a usable credit for `rate.id`
 * means the athlete can book that rate with a package. We deliberately do NOT
 * gate on `availablePaymentMethods` — on the public facility calendar that
 * field is computed without auth and omits "package" for any option that
 * isn't package-only, which previously hid the counter for those options.
 *
 * @param rate    The lesson rate (booking option) being rendered.
 * @param credits Rate-id -> credit map from `getPackageCreditsByLessonRate`,
 *                or undefined when the viewer has no credits (e.g. logged out).
 */
export function getApplicablePackageCredit(
  rate: PackageRate,
  credits: Map<number, PackageCredit> | undefined
): PackageCredit | undefined {
  return credits?.get(rate.id);
}

/**
 * Whether a rate can only be booked with a package the athlete doesn't have yet.
 *
 * True for package-only rates the athlete holds no usable credit for, so the UI
 * can flag them as "Requires Package" instead of offering a direct booking.
 * Callers gate this on the viewer being an eligible (logged-in) athlete.
 *
 * @param rate    The lesson rate (booking option) being rendered.
 * @param credits Rate-id -> credit map, or undefined when the viewer has none.
 */
export function rateRequiresPackage(
  rate: PackageRate,
  credits: Map<number, PackageCredit> | undefined
): boolean {
  return (
    rate.collectType === "only_package" &&
    !getApplicablePackageCredit(rate, credits)
  );
}
